import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Check, X, Loader2, AlertCircle, Building2, User } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function validateCPF(cpf) {
  const d = (cpf || "").replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(d[i]) * (10 - i);
  let rem = (sum * 10) % 11; if (rem === 10) rem = 0;
  if (rem !== parseInt(d[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(d[i]) * (11 - i);
  rem = (sum * 10) % 11; if (rem === 10) rem = 0;
  return rem === parseInt(d[10]);
}

function validateCNPJ(cnpj) {
  const d = (cnpj || "").replace(/\D/g, "");
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
  const weights1 = [5,4,3,2,9,8,7,6,5,4,3,2];
  const weights2 = [6,5,4,3,2,9,8,7,6,5,4,3,2];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(d[i]) * weights1[i];
  let rem = sum % 11; let dig1 = rem < 2 ? 0 : 11 - rem;
  if (dig1 !== parseInt(d[12])) return false;
  sum = 0;
  for (let i = 0; i < 13; i++) sum += parseInt(d[i]) * weights2[i];
  rem = sum % 11; let dig2 = rem < 2 ? 0 : 11 - rem;
  return dig2 === parseInt(d[13]);
}

const fmtCnpjM = (v) => { const d = (v||"").replace(/\D/g,""); return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_,a,b,c,e,f) => f ? `${a}.${b}.${c}/${e}-${f}` : e ? `${a}.${b}.${c}/${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a); };
const fmtCpfM = (v) => { const d = (v||"").replace(/\D/g,""); return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_,a,b,c,e) => e ? `${a}.${b}.${c}-${e}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a); };
const fmtCepM = (v) => { const d = (v||"").replace(/\D/g,"").slice(0,8); if (d.length <= 5) return d; return `${d.slice(0,5)}-${d.slice(5)}`; };

function ReviewField({ label, value, onChange, error, placeholder, mask }) {
  const maxLen = mask === fmtCnpjM ? 14 : mask === fmtCpfM ? 11 : mask === fmtCepM ? 8 : 999;
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <input
        type="text"
        value={mask ? mask(value) : (value || "")}
        onChange={e => onChange(mask ? e.target.value.replace(/\D/g, "").slice(0, maxLen) : e.target.value)}
        placeholder={placeholder || ""}
        className={`mt-0.5 flex h-8 w-full rounded-md border px-3 py-1 text-sm bg-white outline-none focus:ring-1 ${error ? "border-rose-400 focus:ring-rose-300" : "border-slate-200 focus:ring-slate-300"}`}
      />
      {error && <p className="text-[10px] text-rose-500 mt-0.5">{error}</p>}
    </div>
  );
}

export default function AIReviewModal({ open, onClose, aiData, negocioId, onConfirmed }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  const [empresa, setEmpresa] = useState({});
  const [responsavel, setResponsavel] = useState({});
  const [errors, setErrors] = useState({});
  const [validating, setValidating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setLoading(true);
    (async () => {
      let reviewed = null;
      if (negocioId) {
        try {
          const res = await axios.get(`${API}/negocios/${negocioId}`);
          reviewed = res.data?.ai_reviewed_data;
        } catch {}
      }
      if (reviewed && reviewed.empresa) {
        setEmpresa(reviewed.empresa);
        setResponsavel({ ...reviewed.responsavel, data_nascimento: parseDate(reviewed.responsavel?.data_nascimento || "") });
      } else {
        const d = aiData?.dados_extraidos || {};
        const ea = d.empresa || {};
        const ra = d.responsavel || {};
        const end = ea.endereco || {};
        const cs = d.contrato_social || {};
        setEmpresa({
          razao_social: ea.razao_social || cs.razao_social || "",
          nome_fantasia: ea.nome_fantasia || "",
          cnpj: ea.cnpj || ea.cnpj_cartao || cs.cnpj || "",
          logradouro: end.logradouro || "", numero: end.numero || "",
          complemento: end.complemento || "", bairro: end.bairro || "",
          municipio: end.municipio || "", uf: end.uf || "", cep: end.cep || "",
        });
        setResponsavel({ nome: ra.nome || "", cpf: ra.cpf || "", data_nascimento: parseDate(ra.data_nascimento || "") });
      }
      setLoading(false);
    })();
  }, [open]); // eslint-disable-line

  const setE = (k, v) => setEmpresa(prev => ({ ...prev, [k]: v }));
  const setR = (k, v) => setResponsavel(prev => ({ ...prev, [k]: v }));

  const fmtDate = (v) => {
    const d = (v || "").replace(/\D/g, "");
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0,2)}/${d.slice(2)}`;
    return `${d.slice(0,2)}/${d.slice(2,4)}/${d.slice(4,8)}`;
  };
  const parseDate = (v) => {
    // Convert YYYY-MM-DD to DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) { const p = v.split("-"); return `${p[2]}/${p[1]}/${p[0]}`; }
    return v;
  };

  const handleConfirm = async () => {
    const newErrors = {};

    // Validate CPF
    const cpfDigits = responsavel.cpf.replace(/\D/g, "");
    if (cpfDigits && !validateCPF(cpfDigits)) {
      newErrors.cpf = "CPF inválido (dígito verificador incorreto)";
    }

    // Validate CNPJ
    const cnpjDigits = empresa.cnpj.replace(/\D/g, "");
    if (!cnpjDigits || cnpjDigits.length !== 14) {
      newErrors.cnpj = "CNPJ obrigatório (14 dígitos)";
    } else if (!validateCNPJ(cnpjDigits)) {
      newErrors.cnpj = "CNPJ inválido (dígito verificador incorreto)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Corrija os campos destacados.");
      return;
    }

    // Validate CNPJ via BrasilAPI
    setValidating(true);
    try {
      const res = await axios.get(`${API}/validate-cnpj/${cnpjDigits}`);
      if (!res.data.valid) {
        setErrors({ cnpj: res.data.error || "CNPJ não encontrado na Receita Federal." });
        toast.error(res.data.error || "CNPJ não encontrado.");
        setValidating(false);
        return;
      }
      if (!res.data.ativa) {
        setErrors({ cnpj: `Empresa com situação: ${res.data.situacao}` });
        toast.error(`Empresa não está ativa: ${res.data.situacao}`);
        setValidating(false);
        return;
      }
    } catch {
      // API offline — allow with math validation only
    }
    setValidating(false);

    // Save confirmed data
    try {
      await axios.post(`${API}/asaas/save-ai-reviewed/${negocioId}`, {
        empresa: { ...empresa, cnpj: cnpjDigits },
        responsavel: { ...responsavel, cpf: cpfDigits },
      });
      toast.success(`Dados confirmados! ${empresa.razao_social}`);
      if (onConfirmed) onConfirmed({ empresa: { ...empresa, cnpj: cnpjDigits }, responsavel: { ...responsavel, cpf: cpfDigits } });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar.");
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2">
              <Check className="w-5 h-5" style={{ color: tc500 }} />Conferência dos Dados
            </p>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Confira e edite os dados extraídos pela IA antes de salvar.</p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: tc500 }} />
              <p className="text-xs text-slate-400 mt-2">Carregando dados...</p>
            </div>
          ) : (<>
          {/* Empresa */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />Empresa</p>
            <div className="grid grid-cols-2 gap-2">
              <ReviewField label="Razão Social" value={empresa.razao_social} onChange={v => setE("razao_social", v)} />
              <ReviewField label="Nome Fantasia" value={empresa.nome_fantasia} onChange={v => setE("nome_fantasia", v)} />
            </div>
            <ReviewField label="CNPJ" value={empresa.cnpj} onChange={v => setE("cnpj", v)} error={errors.cnpj} mask={fmtCnpjM} placeholder="00.000.000/0000-00" />
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><ReviewField label="Logradouro" value={empresa.logradouro} onChange={v => setE("logradouro", v)} /></div>
              <ReviewField label="Número" value={empresa.numero} onChange={v => setE("numero", v)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <ReviewField label="Complemento" value={empresa.complemento} onChange={v => setE("complemento", v)} />
              <ReviewField label="Bairro" value={empresa.bairro} onChange={v => setE("bairro", v)} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <ReviewField label="Município" value={empresa.municipio} onChange={v => setE("municipio", v)} />
              <ReviewField label="UF" value={empresa.uf} onChange={v => setE("uf", v)} />
              <ReviewField label="CEP" value={empresa.cep} onChange={v => setE("cep", v)} mask={fmtCepM} placeholder="00000-000" />
            </div>
          </div>

          {/* Responsável */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Responsável</p>
            <ReviewField label="Nome completo" value={responsavel.nome} onChange={v => setR("nome", v)} />
            <div className="grid grid-cols-2 gap-2">
              <ReviewField label="CPF" value={responsavel.cpf} onChange={v => setR("cpf", v)} error={errors.cpf} mask={fmtCpfM} placeholder="000.000.000-00" />
              <div>
                <label className="text-xs text-slate-600">Data de Nascimento</label>
                <input type="text" value={fmtDate(responsavel.data_nascimento)} onChange={e => setR("data_nascimento", e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="DD/MM/AAAA" className="mt-0.5 flex h-8 w-full rounded-md border border-slate-200 px-3 py-1 text-sm bg-white outline-none focus:ring-1 focus:ring-slate-300" />
              </div>
            </div>
          </div>
          </>)}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="text-white gap-2" style={{ backgroundColor: tc500 }} disabled={validating} onClick={handleConfirm}>
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Confirmar e avançar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
